import axios from "../middlewares/axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import { appStore } from "../store";
import {
  actionInitApp,
  actionInitModes,
  actionInitDate,
  actionInitWalkingSpeeds,
  actionSetAppLanguage,
  actionSetCalendarDate,
  actionInitDatePickerMinMaxDate,
  actionSetAppLocker,
  actionSetAppPanelSize,
  actionSetUserToken,
  actionUpdateMediaQueries,
  actionSetHash,
  actionSetHeader,
  actionSetConfig,
} from "../actions/app";
import { Route, Router, Switch } from "react-router-dom";
import history from "../history";
import "../scss/app.scss";
import "tippy.js/dist/tippy.css";
import Map from "./Map";
import Board from "./Board";
import Modal from "./Modal";
import { actionBuildTerritoryOutline, actionHandleLegendState } from "../actions/map";
import { getURLSearchParams, isInFrame } from "../services/tools";
import { initGTM } from "../tracking";
import { envVarToBool } from "../services/tools";
import { actionSetNetworkSlider, actionSetNetworkSliderMode } from "../actions/network";
import { message } from "../services/message";
import { ScrollToTop } from "./ScrollToTop";
import { headerDirection } from "../scss/app.scss";

const {
  REACT_APP_HEADER,
  REACT_APP_HEAVY_LINES,
  REACT_APP_LEGEND,
  REACT_APP_SLIDER,
  REACT_APP_TERRITORY_OUTLINE,
  REACT_APP_BOARD_SIZE,
  REACT_APP_API_PROXY_URL,
  REACT_APP_LIBRARY_URL,
} = process.env;

class App extends Component {
  mediaQueries = window.matchMedia("(max-width: 600px)");
  languageFile = null;

  handleMediaQueryChanged = (event) => {
    appStore.dispatch(actionUpdateMediaQueries(event.matches));
  };

  async componentDidMount() {
    const params = getURLSearchParams(history.location);

    // store the token in redux
    if (params.token) {
      appStore.dispatch(actionSetUserToken(params.token));
    }

    // set locker
    appStore.dispatch(actionSetAppLocker(params.lock));

    // set panel size
    const boardSize = params.size ? params.size : params.board ? params.board : REACT_APP_BOARD_SIZE;

    appStore.dispatch(actionSetAppPanelSize(boardSize));

    // set legend
    if (REACT_APP_LEGEND === "all" || (REACT_APP_LEGEND === "web-only" && !this.mediaQueries.matches)) {
      appStore.dispatch(actionHandleLegendState(false));
    }

    // Dispatch action to build territor outline
    if (REACT_APP_TERRITORY_OUTLINE && this.props.options?.config?.territory !== false) {
      axios
        .get(`/api/file?name=territory_outline&folder=map&ext=geojson`)
        .then((response) => appStore.dispatch(actionBuildTerritoryOutline(response.data)));
    }

    try {
      const initParams = { params: {} };

      if (params.touchscreen) {
        initParams.params.touchscreen = params.touchscreen;
      }

      if (params.lang) {
        initParams.params.lang = params.lang;
      }

      if (this.props.options && this.props.options.lang) {
        initParams.params.lang = this.props.options.lang;
      }

      const requestInit = await axios.get("/api/init-application", initParams);
      const init = requestInit.data;

      this.languageFile = init.languageFile;

      // set hash
      appStore.dispatch(actionSetHash(init.hash));
      // set mobile
      init.isMobile = this.mediaQueries.matches;

      // set heavy lines
      init.heavyIds =
        REACT_APP_HEAVY_LINES && JSON.parse(REACT_APP_HEAVY_LINES).length
          ? JSON.parse(REACT_APP_HEAVY_LINES) === true
            ? true
            : JSON.parse(REACT_APP_HEAVY_LINES)
          : [];

      // set modes route-calculation if module exist
      const routeCalculationModule = init.modules.find((m) => m.id === "route-calculation");

      if (envVarToBool(REACT_APP_HEADER)) {
        const response = await fetch(process.env.PUBLIC_URL + "/header.html");
        const header = await response.text();

        appStore.dispatch(actionSetHeader(header));
      }

      if (routeCalculationModule) {
        const modes = {};

        for (const mode of routeCalculationModule.modes) {
          modes[mode.type] = mode.defaultValue !== undefined ? mode.defaultValue : mode.value;
        }

        if (routeCalculationModule.hidePmr === undefined || routeCalculationModule.hidePmr === false) {
          modes["pmr"] = history.location.search.includes("pmr");
        }

        modes["avoid"] = "";
        appStore.dispatch(actionInitModes(modes));

        if (routeCalculationModule.walkingSpeeds !== undefined && routeCalculationModule.walkingSpeeds.length > 0) {
          appStore.dispatch(actionInitWalkingSpeeds(routeCalculationModule.walkingSpeeds));
        }

        let dateSelected = new Date();
        let now = new Date();

        if (
          routeCalculationModule.minDate !== undefined ||
          routeCalculationModule.maxDate !== undefined ||
          routeCalculationModule.defaultDateTime !== undefined
        ) {
          appStore.dispatch(
            actionInitDatePickerMinMaxDate(
              routeCalculationModule.minDate,
              routeCalculationModule.maxDate,
              routeCalculationModule.defaultDateTime
            )
          );

          if (routeCalculationModule.defaultDateTime !== undefined) {
            dateSelected = new Date(routeCalculationModule.defaultDateTime);
            appStore.dispatch(actionSetCalendarDate(new Date(dateSelected)));
          } else {
            if (routeCalculationModule.minDate !== undefined && new Date(routeCalculationModule.minDate) > now) {
              dateSelected = new Date(routeCalculationModule.minDate);
              dateSelected.setHours(10);
              appStore.dispatch(actionSetCalendarDate(dateSelected));
            }

            if (routeCalculationModule.maxDate !== undefined && new Date(routeCalculationModule.maxDate) < now) {
              dateSelected = new Date(routeCalculationModule.maxDate);
              dateSelected.setHours(now.getHours());
              dateSelected.setMinutes(now.getMinutes());
              appStore.dispatch(actionSetCalendarDate(dateSelected));
            }
          }
        }

        // Init Route Calc selected date
        appStore.dispatch(actionInitDate(dateSelected));
      }

      // set language
      appStore.dispatch(actionSetAppLanguage(init.lang));
      // update HTML lang attribute
      document.documentElement.lang = init.lang;

      // Default main DOM element
      init.domElement = "#lcmap";

      const documentReferer = document.referrer ? new URL(document.referrer).origin : null;

      init.documentReferer =
        documentReferer && (isInFrame() || REACT_APP_LIBRARY_URL) ? documentReferer : REACT_APP_API_PROXY_URL;

      init.customMapLines = [];
      init.customMapMarkers = [];

      const { options } = this.props;

      if (options) {
        if (options.route && options.route === "" && options.lines && options.lines.length > 0) {
          init.customMapLines = options.lines;
        }

        if (options.markers && options.markers.length > 0) {
          init.customMapMarkers = options.markers;
        }
      }

      // set everything else
      appStore.dispatch(actionInitApp(init));

      // Slider mode
      if (envVarToBool(REACT_APP_SLIDER) || (REACT_APP_SLIDER && JSON.parse(REACT_APP_SLIDER).modes?.length > 0)) {
        appStore.dispatch(actionSetNetworkSliderMode(true));
      } else {
        appStore.dispatch(actionSetNetworkSlider(false));
      }
    } catch (e) {
      console.warn("Error : app won't init : ", e);
    }

    this.mediaQueries.addListener(this.handleMediaQueryChanged);

    // Google Tag Manager
    initGTM();

    // Send a component loaded message
    message({ loaded: "app" });
  }

  componentWillUnmount() {
    this.mediaQueries && this.mediaQueries.removeListener(this.handleMediaQueryChanged);
  }

  render() {
    const { isMobile, show, modules, options, header, size } = this.props;
    const { pathname, search } = history.location;

    if (Object.keys(modules).length === 1) {
      if (pathname === "/") {
        history.push({
          pathname: `${pathname}${modules[0].id}`,
          search: search.split("&date=")[0],
        });
      }
    }

    if (options?.route && pathname === "/") {
      history.push((!options.route.startsWith("/") ? "/" : "") + options.route);
    } else if (options?.menu?.links) {
      const modulesToHide = Object.keys(options.menu.links).filter((link) => !options.menu.links[link]);

      if (modules.length - modulesToHide.length === 1) {
        const { pathname } = history.location;
        const modulesToShow = modules.map((m) => (m.id === "thematic" ? m.data : m.id));
        const moduleToRedirect = modulesToShow.find((m) => !modulesToHide.includes(m));

        if (pathname === "/") {
          history.push({
            pathname: `${pathname}${moduleToRedirect}`,
          });
        }
      }
    }

    // Edit app config if pass as params
    if (options?.config) {
      appStore.dispatch(actionSetConfig(options.config));
    }

    let showBoard = true;

    if (
      modules.length === 0 ||
      !size ||
      (options && options.menu && options.menu.show !== undefined && options.menu.show === false)
    ) {
      showBoard = false;
    }

    // if (options !== undefined && options.route !== undefined && options.route === "") {
    //   showBoard = false;
    // }

    return (
      <>
        {envVarToBool(REACT_APP_HEADER) && (
          <header
            className={
              (headerDirection === "column" || isMobile ? "lc-header-column" : "lc-header-row") +
              (isMobile ? "-mobile" : "")
            }
            dangerouslySetInnerHTML={{ __html: header }}
          />
        )}
        <Router history={history}>
          <Map startOnMobile={this.mediaQueries.matches} showBoard={showBoard} />
          <Switch>
            {modules.map((module, index) => (
              <Route
                key={module.id + "_" + index}
                path={`/${["thematic", "text-board", "projects"].includes(module.id) ? module.data : module.id}`}
                component={() => <Board module={module} options={options} showBoard={showBoard} />}
              />
            ))}
            <Route
              component={() => (
                <Board isMobileBoot={this.mediaQueries.matches} options={options} showBoard={showBoard} />
              )}
            />
          </Switch>
          {show && <Modal />}
          {(!options || options?.features?.scrollToTop) && <ScrollToTop />}
        </Router>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    isMobile: state.app.isMobile,
    modules: state.app.modules,
    show: state.modal.show,
    header: state.app.header,
    size: state.app.size,
  };
};

export default connect(mapStateToProps)(App);
